import React, {
  useCallback, useMemo, useState,
} from 'react';

// styles used by the others components.
import { forOwn, isEmpty, size } from 'lodash';
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types';
import {
  QuestionLabel,
  TextError,
  Container,
  OptionContainer,
  SubText,
} from '../styles';

// styles local
import {
  Checkbox,
} from './styles';
import InputOtherDescription, { ERROR_TEXT_INPUT_OTHER } from '../input-other-description';
import { OPTION_TYPE_VALUE } from '../constants';

const MultipleQuestionType = (props) => {
  const {
    questionOptions,
    label,
    errorText,
    errorField,
    onChange,
    selectedValue,
    isScreener,
    multipleSelectionArray,
  } = props;
  const [showInputOther, setShowInputOther] = useState(false);

  const handleClickCheckbox = useCallback((idQuestion, optionType) => {
    if (isEmpty(selectedValue)) {
      onChange({ [idQuestion]: true });
      if (optionType === OPTION_TYPE_VALUE) {
        setShowInputOther(true);
      }
    } else {
      const newSelected = {
        ...selectedValue,
        [idQuestion]: !selectedValue[idQuestion],
      };
      const filteredSelected = {};

      forOwn(newSelected, (v, k) => {
        if (v) {
          filteredSelected[k] = v;
          if (k === idQuestion && optionType === OPTION_TYPE_VALUE) {
            setShowInputOther(true);
          }
        } else if (k === idQuestion && optionType === OPTION_TYPE_VALUE) {
          setShowInputOther(false);
        }
      });

      onChange({ ...filteredSelected });
    }
  }, [onChange, selectedValue]);

  const isInputOther = useMemo(() => questionOptions
    .some(({ id, optionType }) => optionType === OPTION_TYPE_VALUE
      && Object.prototype.hasOwnProperty.call(selectedValue, id)), [questionOptions, selectedValue]);

  const isShowErrorOtherOption = useMemo(() => errorField && isInputOther
    && showInputOther && isEmpty(errorField?.answer?.optionDescription), [errorField, isInputOther, showInputOther]);

  const isMultipleSelectionArrayError = useMemo(() => {
    if (!isEmpty(errorField.multipleSelectionArray)) {
      if (size(errorField.multipleSelectionArray) === 1) {
        return `Please select exactly ${errorField.multipleSelectionArray[0] === 1 ? '1 option'
          : `${errorField.multipleSelectionArray[0]} options`} for this question`;
      }
      if (size(errorField.multipleSelectionArray) === 2) {
        return `Please select between ${errorField.multipleSelectionArray[0]} and `
          + `${errorField.multipleSelectionArray[1]} options for this question`;
      }
    }
    return null;
  }, [errorField]);

  return (
    <Container>
      <QuestionLabel>{label}</QuestionLabel>
      {
        multipleSelectionArray && multipleSelectionArray.length > 1
        && (
          <SubText>
            {`Please select between ${multipleSelectionArray[0]} to ${multipleSelectionArray[1]} options from the list below.`}
          </SubText>
        )
      }
      {
        multipleSelectionArray && multipleSelectionArray.length === 1
        && (
          <SubText>
            {
              `Please select ${multipleSelectionArray[0] === 1 ? '1 option'
                : `exactly ${multipleSelectionArray[0]} options`} from the list below.`
            }
          </SubText>
        )
      }
      {questionOptions.map(({ id, label: labelOption, optionType }) => (
        <OptionContainer optionType={optionType}>
          <Checkbox
            data-selection={labelOption}
            key={id}
            label={labelOption}
            onChange={() => handleClickCheckbox(id, optionType)}
            checked={!isEmpty(selectedValue) && !!selectedValue[id]}
            data-multiple-select-checkbox={labelOption}
          />
          {(optionType === OPTION_TYPE_VALUE && !isScreener) && (
            <InputOtherDescription
              showInputOther={showInputOther}
              onChange={onChange}
              selectedValue={selectedValue}
              errorField={isShowErrorOtherOption ? errorField : ''}
            />
          )}
        </OptionContainer>
      ))}

      {errorField && !isShowErrorOtherOption && (
        <TextError data-tester-test-error>
          {isMultipleSelectionArrayError || errorText}
        </TextError>
      )}

      {isShowErrorOtherOption && (
        <TextError data-tester-test-error>
          {ERROR_TEXT_INPUT_OTHER}
        </TextError>
      )}

    </Container>
  );
};

MultipleQuestionType.propTypes = {
  label: string.isRequired,
  errorText: string,
  questionOptions: arrayOf(shape({
    label: string,
    id: string,
    optionType: string,
  })).isRequired,
  errorField: bool,
  onChange: func,
  selectedValue: shape({}),
  isScreener: bool.isRequired,
  multipleSelectionArray: arrayOf(number).isRequired,
};

MultipleQuestionType.defaultProps = {
  errorText: 'Please select an answer',
  errorField: false,
  onChange: () => { },
  selectedValue: {},
};

export default MultipleQuestionType;
